import { type ReactElement, useEffect, useRef, useState } from 'react';
import { FARE_CONTEST_REASON_CODES, type DisputeListItem, type DisputeState } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { disputeActions, loadDisputeQueue, screenAllowed, parseMinorAmount, isCurrencyCode, formatWireMoney, formatMarketInstant } from '../../src/index';
import { useScopeZone } from '../components/scope-zone';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { IdRef } from '../components/code-ref';
import { PersonName } from '../components/person-name';
import { DrawerStack } from '../components/drawer-stack';
import { CentralLink } from '../components/central-link';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerSection, Field, MoneyInput, Notice, PageHeader, Panel, Select, Table, TextInput, FormRow, FormGrid, EmptyState, Ink, KeyValueList, Row, Grid, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';

/** D469 (X-ray A-08): the stable reasons of a dispute credit (the server takes any upper-case stable token). */
const DISPUTE_CREDIT_REASONS = ['FARE_ADJUSTED', 'ROUTE_ADJUSTED', 'DUPLICATE_CHARGE', 'GOODWILL', 'OTHER'] as const;

/**
 * Disputas (D318) — operator triage of post-trip disputes over the real M13 collaboration surface: the queue
 * (`GET /v1/disputes`, open + under review, oldest first), a lookup by id, and the kernel-governed actions —
 * **iniciar revisão** (`open → under_review`), **resolver com crédito** (a signed credit folded into the ride's
 * final charge, money as minor units + ISO 4217) and **indeferir**. What the operator may do next comes from the
 * real `@vello/ride-collaboration` transitions, so the console never offers an edge the server would refuse.
 */
const STATE_TONE: Readonly<Record<DisputeState, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  open: 'warning',
  under_review: 'neutral',
  resolved: 'success',
  rejected: 'danger',
};

export function DisputesScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { v, fail } = useVocab();
  // D469 (X-ray A-08): the credit in the RIDE's currency, never typed — entered through MoneyInput, at most the fare.
  const [creditMinor, setCreditMinor] = useState<bigint | undefined>(undefined);
  // D420 (Admin checklist #2): a dispute carries no market, so it renders in the operator scope's zone (UTC, marked, for
  // a platform scope) — never a sliced ISO string that reads as local time.
  const { zone, lang } = useScopeZone();
  const whenMs = (ms: number): string => formatMarketInstant(new Date(ms).toISOString(), zone, lang);
  // D448 (R-26): a rider's contest carries a stable reason code — shown in words; an older free-text reason as sent.
  const disputeReasonLabel = (d: DisputeListItem): string => {
    const code = d.reasonCode ?? d.dispute.reason;
    return (FARE_CONTEST_REASON_CODES as readonly string[]).includes(code) ? v('ride_collaboration.fare_contest_reason', code) : code;
  };
  const [items, setItems] = useState<readonly DisputeListItem[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [refresh, setRefresh] = useState(0);

  const [lookup, setLookup] = useState('');
  const [selected, setSelected] = useState<DisputeListItem | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lookupError, setLookupError] = useState<string | undefined>(undefined);
  const [credit, setCredit] = useState('0');
  const [currency, setCurrency] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // A background tick bumps `refresh` with `quiet` set: the queue stays on screen (no loading block) while it is re-read.
  const quiet = useRef(false);
  useEffect(() => {
    let cancelled = false;
    if (!quiet.current) setLoading(true);
    quiet.current = false;
    setError(undefined);
    void loadDisputeQueue(runtime).then((res) => {
      if (cancelled) return;
      if (res.ok) setItems(res.value.disputes);
      else {
        setItems([]);
        setError(t('disq.error.queue', { code: fail(res.error) }));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, refresh, t]);
  useAutoRefresh(() => {
    quiet.current = true;
    setRefresh((n) => n + 1);
  }, { intervalMs: AUTO_REFRESH_MS.queue });

  // One door to the drawer — a queue row or a dispute found by id — so the form starts clean for the dispute shown.
  const openDispute = (d: DisputeListItem): void => {
    setSelected(d);
    setMsg(undefined);
    setCreditMinor(undefined);
    setCredit('0');
    setCurrency('');
    setReasonCode('');
    setDrawerOpen(true);
  };

  const find = async (): Promise<void> => {
    setLookupError(undefined);
    const res = await runtime.rideCollaboration.getDispute(lookup.trim());
    if (res.ok) openDispute(res.value);
    else setLookupError(t('disq.error.lookup', { code: fail(res.error) }));
  };

  const review = async (): Promise<void> => {
    if (selected === undefined) return;
    setBusy(true);
    const res = await runtime.rideCollaboration.reviewDispute(selected.disputeId);
    setBusy(false);
    if (res.ok) {
      setSelected({ ...selected, dispute: res.value.dispute });
      setMsg({ tone: 'success', text: t('disq.reviewing') });
      setRefresh((n) => n + 1);
    } else setMsg({ tone: 'danger', text: t('ui.actionFailed', { code: fail(res.error) }) });
  };

  const resolveCredit = async (): Promise<void> => {
    if (selected === undefined || selected.fare === undefined || selected.fare === null || creditMinor === undefined) return;
    setBusy(true);
    const res = await runtime.rideCollaboration.resolveDisputeCredit(selected.disputeId, { creditMinor: creditMinor.toString(), reasonCode });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: res.error.reasonCode === 'DISPUTE_CREDIT_ABOVE_FARE' ? t('disq.credit.aboveFare') : t('ui.actionFailed', { code: fail(res.error) }) });
      return;
    }
    setSelected({ ...selected, dispute: res.value.dispute });
    setMsg({ tone: 'success', text: res.value.approval?.status === 'PENDING' ? t('disq.credit.pending') : t('disq.resolved', { credit: formatWireMoney({ amountMinor: creditMinor.toString(), currency: selected.fare.currency }) }) });
    setRefresh((n) => n + 1);
  };

  const resolve = async (): Promise<void> => {
    if (selected === undefined) return;
    if (selected.fare !== undefined) return resolveCredit();
    const amountMinor = parseMinorAmount(credit);
    if (amountMinor === undefined || !isCurrencyCode(currency)) {
      setMsg({ tone: 'danger', text: t('disq.error.credit') });
      return;
    }
    setBusy(true);
    const res = await runtime.rideCollaboration.resolveDispute(selected.disputeId, { amountMinor, currency: currency.trim() }, reasonCode.trim());
    setBusy(false);
    if (res.ok) {
      setSelected({ ...selected, dispute: res.value.dispute });
      const credited = res.value.adjustment !== undefined ? formatWireMoney(res.value.adjustment.amount) : t('disq.noCredit');
      setMsg({ tone: 'success', text: t('disq.resolved', { credit: credited }) });
      setRefresh((n) => n + 1);
    } else setMsg({ tone: 'danger', text: t('ui.actionFailed', { code: fail(res.error) }) });
  };

  const reject = async (): Promise<void> => {
    if (selected === undefined) return;
    setBusy(true);
    const res = await runtime.rideCollaboration.rejectDispute(selected.disputeId);
    setBusy(false);
    if (res.ok) {
      setSelected({ ...selected, dispute: res.value.dispute });
      setMsg({ tone: 'success', text: t('disq.rejected') });
      setRefresh((n) => n + 1);
    } else setMsg({ tone: 'danger', text: t('ui.actionFailed', { code: fail(res.error) }) });
  };

  const actions = selected !== undefined ? disputeActions(selected.dispute) : undefined;

  const canOpenRide = screenAllowed(grants, 'ride_detail');
  const fareOk = selected?.fare !== undefined && selected.fare !== null;

  return (
    <>
      <PageHeader title={t('disq.title')} subtitle={t('disq.subtitle')} />

      {/* Opens a dispute that is not in the queue (already closed, say) in the same drawer. */}
      <Panel>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void find();
          }}
        >
          <FormRow
            action={
              <Button type="submit" variant="secondary" disabled={lookup.trim() === ''}>{t('disq.lookup')}</Button>
            }
          >
            <Field label={t('disq.field.id')} htmlFor="disq-id">
              <TextInput id="disq-id" className="tnum" value={lookup} placeholder={t('disq.field.id.placeholder')} onChange={(e) => setLookup(e.target.value)} />
            </Field>
          </FormRow>
        </form>
        {lookupError !== undefined && <Notice tone="danger">{lookupError}</Notice>}
      </Panel>

      {error !== undefined && (
        <div>
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <Table
        columns={[
          { key: 'id', header: t('disq.col.dispute'), render: (d: DisputeListItem) => <IdRef value={d.disputeId} /> },
          { key: 'ride', header: t('disq.col.ride'), render: (d: DisputeListItem) => <IdRef value={d.dispute.rideId} /> },
          { key: 'reason', header: t('disq.col.reason'), render: (d: DisputeListItem) => disputeReasonLabel(d) },
          { key: 'state', header: t('disq.col.state'), render: (d: DisputeListItem) => <Badge tone={STATE_TONE[d.dispute.state]}>{v('ride_collaboration.dispute_state', d.dispute.state)}</Badge> },
          { key: 'opened', header: t('disq.col.opened'), render: (d: DisputeListItem) => <span className="tnum">{whenMs(d.dispute.openedAtMs)}</span> },
        ]}
        rows={items ?? []}
        rowKey={(d) => d.disputeId}
        onRowClick={openDispute}
        {...(drawerOpen && selected !== undefined ? { selectedKey: selected.disputeId } : {})}
        loading={loading}
        loadingLabel={t('ui.loading')}
        empty={<EmptyState title={t('disq.queue.empty')} />}
      />

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} labelledBy="disq-drawer-title">
        {selected !== undefined && actions !== undefined && (
          <>
            <DrawerHeader titleId="disq-drawer-title" title={t('disq.col.dispute')} actions={<Badge tone={STATE_TONE[selected.dispute.state]}>{v('ride_collaboration.dispute_state', selected.dispute.state)}</Badge>} onClose={() => setDrawerOpen(false)} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                <CentralLink kind="dispute" recordId={selected.disputeId} />
                <KeyValueList
                  items={[
                    { label: t('disq.col.dispute'), value: <IdRef value={selected.disputeId} copy full /> },
                    {
                      label: t('disq.col.ride'),
                      value: (
                        <Row gap={2} align="center" wrap>
                          <IdRef value={selected.dispute.rideId} copy full />
                          {canOpenRide && <Button size="sm" variant="secondary" icon="arrowRight" onClick={() => navigate('ride_detail', { id: selected.dispute.rideId })}>{t('disq.detail.openRide')}</Button>}
                        </Row>
                      ),
                    },
                    { label: t('disq.col.reason'), value: disputeReasonLabel(selected) },
                    { label: t('disq.col.opened'), value: whenMs(selected.dispute.openedAtMs), mono: true },
                    ...(selected.dispute.resolvedAtMs !== undefined ? [{ label: t('disq.detail.closedAt'), value: whenMs(selected.dispute.resolvedAtMs), mono: true }] : []),
                    ...(selected.riderId !== undefined ? [{ label: t('disq.detail.openedBy'), value: <PersonName id={selected.riderId} /> }] : []),
                    ...(selected.fare !== undefined && selected.fare !== null ? [{ label: t('disq.detail.fare'), value: formatWireMoney(selected.fare), mono: true }] : []),
                  ]}
                />
                {/* D448 (R-26): the rider's own words, when the contest came from the receipt. */}
                {selected.comment !== undefined && (
                  <DrawerSection title={t('disq.detail.commentTitle')}>
                    <Ink as="p" preLine>{selected.comment}</Ink>
                  </DrawerSection>
                )}

                {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}

                {!actions.terminal && (
                  <DrawerSection title={t('disq.detail.resolution')}>
                    {selected.fare !== undefined ? (
                      <FormGrid>
                        {selected.fare !== null && isIso4217Currency(selected.fare.currency) ? (
                          <MoneyInput id="disq-credit-money" label={t('disq.field.credit')} currency={selected.fare.currency} locale={lang} valueMinor={creditMinor} onChangeMinor={setCreditMinor} issueText={(i) => v('format.money_input_issue', i)} />
                        ) : (
                          <Notice tone="warning">{t('disq.credit.noFare')}</Notice>
                        )}
                        <Field label={t('disq.field.reasonCode')} htmlFor="disq-credit-reason" hint={selected.fare !== null ? t('disq.credit.upTo', { fare: formatWireMoney(selected.fare) }) : undefined}>
                          <Select id="disq-credit-reason" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                            <option value="">—</option>
                            {DISPUTE_CREDIT_REASONS.map((r) => (
                              <option key={r} value={r}>{v('ride_collaboration.dispute_credit_reason', r)}</option>
                            ))}
                          </Select>
                        </Field>
                      </FormGrid>
                    ) : (
                      <Grid columns={3} gap={3}>
                        <Field label={t('disq.field.credit')} htmlFor="disq-credit" hint={t('disq.field.credit.hint')}>
                          <TextInput id="disq-credit" className="tnum" inputMode="numeric" value={credit} onChange={(e) => setCredit(e.target.value)} />
                        </Field>
                        <Field label={t('disq.field.currency')} htmlFor="disq-currency" hint="ISO 4217">
                          <TextInput id="disq-currency" value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
                        </Field>
                        <Field label={t('disq.field.reasonCode')} htmlFor="disq-reason">
                          <TextInput id="disq-reason" value={reasonCode} placeholder={t('disq.field.reasonCode.placeholder')} onChange={(e) => setReasonCode(e.target.value)} />
                        </Field>
                      </Grid>
                    )}
                  </DrawerSection>
                )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              {actions.terminal ? (
                <Notice tone="neutral">{t('disq.terminal')}</Notice>
              ) : (
                <>
                  {actions.review && <Button variant="secondary" block disabled={busy} onClick={() => void review()}>{t('disq.review')}</Button>}
                  <Button variant="danger" block disabled={busy || !actions.reject} onClick={() => void reject()}>{t('disq.reject')}</Button>
                  <Button variant="primary" block disabled={busy || !actions.resolve || reasonCode.trim() === '' || (selected.fare !== undefined ? !fareOk || creditMinor === undefined || creditMinor > BigInt(selected.fare?.amountMinor ?? '0') : !isCurrencyCode(currency) || parseMinorAmount(credit) === undefined)} onClick={() => void resolve()}>
                    {t('disq.resolve')}
                  </Button>
                </>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
